import { useState, useEffect, useMemo } from "react";
import { useLanguage } from "./LanguageContext";
import { db } from "../lib/supabase";

interface NavDataPoint {
  month: string;
  year: number;
  value: number;
  monthKey: string;
}

interface NavChartProps {
  className?: string;
  onChartClick?: () => void;
}

interface TooltipProps {
  visible: boolean;
  x: number;
  y: number;
  data: NavDataPoint | null;
}

// Elegant Point component with smooth interactions
interface PointProps {
  cx: number;
  cy: number;
  data: NavDataPoint;
  onHover: (data: NavDataPoint, event: React.MouseEvent) => void;
  onMove: (event: React.MouseEvent) => void;
  onLeave: () => void;
  isHovered: boolean;
}

function Point({ cx, cy, data, onHover, onMove, onLeave, isHovered }: PointProps) {
  return (
    <g className="transition-all duration-300 ease-out">
      {/* Base point - subtle presence */}
      <circle
        cx={cx}
        cy={cy}
        r={isHovered ? 0 : 2}
        fill="#16A34A"
        className="transition-all duration-300 ease-out"
        style={{ opacity: isHovered ? 0 : 0.8 }}
      />
      
      {/* Hover state - elegant glow effect */}
      {isHovered && (
        <>
          {/* Outer glow */}
          <circle
            cx={cx}
            cy={cy}
            r={8}
            fill="#16A34A"
            opacity={0.1}
            className="animate-pulse"
          />
          {/* Main hover point */}
          <circle
            cx={cx}
            cy={cy}
            r={4}
            fill="#16A34A"
            className="transition-all duration-300 ease-out"
            style={{
              filter: 'drop-shadow(0 0 12px rgba(22, 163, 74, 0.4))'
            }}
          />
          {/* Inner highlight */}
          <circle
            cx={cx}
            cy={cy}
            r={1.5}
            fill="rgba(255, 255, 255, 0.9)"
            className="transition-all duration-300 ease-out"
          />
        </>
      )}
      
      {/* Invisible hover area */}
      <circle
        cx={cx}
        cy={cy}
        r={16}
        fill="transparent"
        className="cursor-pointer"
        onMouseEnter={(e) => onHover(data, e)}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      />
    </g>
  );
}

// Premium Tooltip component - Same style as SimpleDonutChart
function Tooltip({ visible, x, y, data }: TooltipProps) {
  const { t, language } = useLanguage();
  
  if (!visible || !data) return null;

  const formatValue = (value: number) => {
    const rounded = Math.round(value);
    if (language === 'es') {
      return `€${rounded.toLocaleString('es-ES')}`;
    }
    return `€${rounded.toLocaleString('en-US')}`;
  };

  return (
    <div
      className="fixed z-[9999] pointer-events-none"
      style={{
        left: x,
        top: y,
        transform: y > 100 ? 'translate(-50%, -100%)' : 'translate(-50%, 0%)'
      }}
    >
      <div
        className="relative px-6 py-4 rounded-xl shadow-2xl backdrop-blur-md border border-white/20"
        style={{
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.97) 0%, rgba(30, 41, 59, 0.97) 100%)',
          animation: 'tooltipFadeIn 150ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.35), 0 10px 20px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.12)',
          minWidth: '140px'
        }}
      >
        <div className="text-xs font-medium text-gray-300 mb-2.5 opacity-95 leading-tight tracking-wide">
          {t(data.monthKey)} {data.year}
        </div>
        <div 
          className="font-bold text-white text-xl tracking-tight"
          style={{ 
            fontFeatureSettings: '"tnum"',
            letterSpacing: '-0.03em'
          }}
        >
          {formatValue(data.value)}
        </div>
        
        {/* Tooltip Arrow Premium - Dynamic */}
        <div
          className={`absolute left-1/2 transform -translate-x-1/2 ${
            y > 100 ? 'top-full' : 'bottom-full'
          }`}
          style={{
            width: 0,
            height: 0,
            borderLeft: '9px solid transparent',
            borderRight: '9px solid transparent',
            ...(y > 100 
              ? { borderTop: '9px solid rgba(15, 23, 42, 0.97)' }
              : { borderBottom: '9px solid rgba(15, 23, 42, 0.97)' }
            ),
            filter: 'drop-shadow(0 3px 6px rgba(0, 0, 0, 0.25))'
          }}
        />
        
        {/* Subtle glow effect */}
        <div 
          className="absolute inset-0 rounded-xl"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 60%)',
            pointerEvents: 'none'
          }}
        />
      </div>
    </div>
  );
}

export function NavChart({ className = "", onChartClick }: NavChartProps) {
  const { t, language } = useLanguage();
  const [hoveredPoint, setHoveredPoint] = useState<NavDataPoint | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [isAnimated, setIsAnimated] = useState(false);
  const [remoteNav, setRemoteNav] = useState<NavDataPoint[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  // NAV data - Exact values specified
  const navData: NavDataPoint[] = [
    { month: 'may', year: 2024, value: 42000, monthKey: 'm.may' },
    { month: 'jun', year: 2024, value: 42000, monthKey: 'm.jun' },
    { month: 'jul', year: 2024, value: 42000, monthKey: 'm.jul' },
    { month: 'aug', year: 2024, value: 300000, monthKey: 'm.aug' },
    { month: 'sep', year: 2024, value: 367000, monthKey: 'm.sep' },
    { month: 'oct', year: 2024, value: 391250, monthKey: 'm.oct' },
    { month: 'nov', year: 2024, value: 425000, monthKey: 'm.nov' },
    { month: 'dec', year: 2024, value: 425000, monthKey: 'm.dec' },
    { month: 'jan', year: 2025, value: 507650, monthKey: 'm.jan' },
    { month: 'feb', year: 2025, value: 549871.2, monthKey: 'm.feb' },
    { month: 'mar', year: 2025, value: 588714.4, monthKey: 'm.mar' },
    { month: 'apr', year: 2025, value: 591214.4, monthKey: 'm.apr' },
    { month: 'may', year: 2025, value: 657850.8, monthKey: 'm.may' },
    { month: 'jun', year: 2025, value: 695705.8, monthKey: 'm.jun' },
    { month: 'jul', year: 2025, value: 708665.8, monthKey: 'm.jul' },
    { month: 'aug', year: 2025, value: 708665.8, monthKey: 'm.aug' }
  ];

  // Fetch nav months from the DB and map them to NavDataPoint
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);

    db.getNavMonths()
      .then((rows) => {
        if (!mounted) return;
        // rows is NavGeneral[]; map to NavDataPoint
        const shortMonthAbbr = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
        const monthKeyMap = ['m.jan','m.feb','m.mar','m.apr','m.may','m.jun','m.jul','m.aug','m.sep','m.oct','m.nov','m.dec'];

        // Safely parse year/month from server values without using Date
        const parseYearMonth = (dateStr?: string) => {
          if (!dateStr) {
            const now = new Date();
            return { year: now.getFullYear(), monthIdx: now.getMonth() };
          }
          // Accept formats like 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:MM:SSZ'
          const ym = dateStr.slice(0,7);
          const parts = ym.split('-');
          const year = parseInt(parts[0], 10) || new Date().getFullYear();
          const month = parseInt(parts[1], 10);
          const monthIdx = Number.isFinite(month) ? Math.max(0, Math.min(11, month - 1)) : 0;
          return { year, monthIdx };
        };

        const mapped: NavDataPoint[] = (rows || []).map(r => {
          const dateStr = r.period_start || r.period_end || undefined;
          const { year, monthIdx } = parseYearMonth(dateStr);
          return {
            month: shortMonthAbbr[monthIdx],
            year,
            value: Number(r.value ?? 0),
            monthKey: monthKeyMap[monthIdx]
          };
        });

        // Ensure we sort by year/month ascending (oldest -> newest)
        mapped.sort((a,b) => (a.year - b.year) || (shortMonthAbbr.indexOf(a.month) - shortMonthAbbr.indexOf(b.month)));

        setRemoteNav(mapped);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err);
        // keep fallback navData
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => { mounted = false; };
  }, []);

  // Build last 12 calendar months and map `navData` onto them. Missing
  // months will be filled with zero-value entries so the chart always
  // shows a 12-month series (oldest -> newest).
  const buildLast12Months = () => { 
    const now = new Date();
    return Array.from({ length: 12 }).map((_, idx) => {
      const monthsBack = 11 - idx;
      return new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
    });
  };

  const shortMonthAbbr = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
  const monthKeyMap = ['m.jan','m.feb','m.mar','m.apr','m.may','m.jun','m.jul','m.aug','m.sep','m.oct','m.nov','m.dec'];

  // baseNav prefers remoteNav (from DB) and falls back to the sample navData
  const baseNav = remoteNav && remoteNav.length > 0 ? remoteNav : navData;

  // Build the full month range from earliest -> latest available in `baseNav`.
  // If there's no data, fallback to last 12 calendar months.
  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all');

  const availableYears = useMemo(() => {
    if (!baseNav || baseNav.length === 0) return [] as number[];
    const years = Array.from(new Set(baseNav.map(b => b.year))).sort((a,b) => a - b);
    return years;
  }, [baseNav]);

  // If the selected year becomes unavailable (e.g., data changes), reset to 'all'
  useEffect(() => {
    if (selectedYear !== 'all' && !availableYears.includes(selectedYear as number)) {
      setSelectedYear('all');
    }
  }, [availableYears, selectedYear]);

  const monthsRangeDates = (() => {
    if (baseNav && baseNav.length > 0) {
      // Sort ascending by year+month
      const sorted = [...baseNav].sort((a, b) =>
        a.year - b.year || shortMonthAbbr.indexOf(a.month) - shortMonthAbbr.indexOf(b.month)
      );
      const earliest = sorted[0];
      const latest = sorted[sorted.length - 1];
      const start = new Date(earliest.year, shortMonthAbbr.indexOf(earliest.month), 1);
      const end = new Date(latest.year, shortMonthAbbr.indexOf(latest.month), 1);

      const months: Date[] = [];
      for (let d = new Date(start); d <= end; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
        months.push(new Date(d.getFullYear(), d.getMonth(), 1));
      }
      // If a year filter is active, filter the months
      if (selectedYear !== 'all') {
        return months.filter(m => m.getFullYear() === selectedYear);
      }
      return months;
    }
    // fallback: last 12 months
    return buildLast12Months();
  })();

  const findByMonth = (d: Date) => {
    const mon = shortMonthAbbr[d.getMonth()];
    const yr = d.getFullYear();
    return baseNav.find(nd => nd.month === mon && nd.year === yr) || null;
  };

  const monthsData: NavDataPoint[] = monthsRangeDates.map(d => {
    const found = findByMonth(d);
    if (found) return found;
    const idx = d.getMonth();
    return {
      month: shortMonthAbbr[idx],
      year: d.getFullYear(),
      value: 0,
      monthKey: monthKeyMap[idx]
    };
  });

  // Use the mapped 12-month series as the chart's data source
  const data = monthsData;

  // Responsive chart dimensions: adapt for wide screens
  const [windowWidth, setWindowWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const chartWidth = useMemo(() => Math.min(1400, Math.max(700, Math.floor(windowWidth * 0.88))), [windowWidth]);
  const chartHeight = useMemo(() => (windowWidth > 1400 ? 360 : 300), [windowWidth]);
  const margin = useMemo(() => ({ top: 30, right: 40, bottom: 50, left: windowWidth > 1400 ? 110 : 90 }), [windowWidth]);

  const plotWidth = chartWidth - margin.left - margin.right;
  const plotHeight = chartHeight - margin.top - margin.bottom;

  // Calculate value range with padding for elegance (based on the 12-month series)
  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const valueRange = maxValue - minValue;
  const padding = valueRange * 0.05; // 5% padding for breathing room

  const getPointPosition = (value: number, index: number) => {
    // Normalize value to 0-1 range (0 = minValue, 1 = maxValue)
    const normalizedValue = valueRange > 0 
      ? (value - minValue) / valueRange 
      : 0;
    const x = (index / (data.length - 1)) * plotWidth + margin.left;
    // Y coordinate: higher values go to top, lower values to bottom
    const y = margin.top + plotHeight - (normalizedValue * plotHeight);
    return { x, y };
  };

  // Create elegant smooth curve
  const createSmoothPath = (points: { x: number; y: number }[]) => {
    if (points.length < 2) return '';
    
    let path = `M ${points[0].x} ${points[0].y}`;
    
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const smoothing = 0.25; // Smooth but not too curved
      
      const cpx1 = prev.x + (curr.x - prev.x) * smoothing;
      const cpy1 = prev.y;
      const cpx2 = curr.x - (curr.x - prev.x) * smoothing;
      const cpy2 = curr.y;
      
      path += ` C ${cpx1} ${cpy1}, ${cpx2} ${cpy2}, ${curr.x} ${curr.y}`;
    }
    
    return path;
  };

  const points = data.map((d, i) => getPointPosition(d.value, i));
  const pathData = createSmoothPath(points);

  // Elegant area under curve
  const areaPath = pathData + ` L ${margin.left + plotWidth} ${margin.top + plotHeight} L ${margin.left} ${margin.top + plotHeight} Z`;

  // Y-axis ticks with smart formatting (bottom to top)
  const yTicks = Array.from({ length: 5 }, (_, i) => {
    const value = minValue + (valueRange * i) / 4;
    return { 
      value, 
      label: value >= 1000 ? `€${Math.round(value / 1000)}K` : `€${Math.round(value)}`
    };
  });

  const handlePointHover = (data: NavDataPoint, event: React.MouseEvent) => {
    setHoveredPoint(data);
    updateTooltipPosition(event);
  };

  const handlePointMove = (event: React.MouseEvent) => {
    if (hoveredPoint) {
      updateTooltipPosition(event);
    }
  };

  const updateTooltipPosition = (event: React.MouseEvent) => {
    const tooltipHeight = 80; // Altura estimada del tooltip
    const margin = 16; // Margen desde el cursor
    
    // Detectar si el tooltip se cortaría en la parte superior
    const wouldClipTop = event.clientY - tooltipHeight - margin < 20;
    
    setTooltipPos({
      x: event.clientX,
      y: wouldClipTop 
        ? event.clientY + margin // Mostrar debajo del cursor si se cortaría arriba
        : event.clientY - margin // Mostrar arriba del cursor normalmente
    });
  };

  const handlePointLeave = () => {
    setHoveredPoint(null);
  };

  // Slow, elegant animation trigger
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimated(true);
    }, 200); // Slightly longer delay for dramatic effect
    return () => clearTimeout(timer);
  }, []);

  // Calculate performance metrics
  const startValue = data[0]?.value || 0;
  const endValue = data[data.length - 1]?.value || 0;
  const totalGrowth = startValue > 0 ? ((endValue - startValue) / startValue) * 100 : 0;

  return (
    <>
      <div 
        role={onChartClick ? 'button' : undefined}
        tabIndex={onChartClick ? 0 : undefined}
        onClick={onChartClick}
        className={`bg-white border border-[#E8EDF4] rounded-2xl overflow-hidden shadow-premium hover:shadow-premium-hover transition-all duration-500 ${onChartClick ? 'cursor-pointer' : ''} ${className}`}
      >
        {/* Elegant header */}
        <div className="p-6 pb-4 border-b border-gray-50">
          <div className="flex items-start justify-between">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              {t('nav.title')}
            </h3>
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600">Año</label>
              <select
                value={selectedYear === 'all' ? 'all' : String(selectedYear)}
                onChange={(e) => setSelectedYear(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                className="border rounded px-2 py-1 text-sm"
              >
                <option value="all">Todos</option>
                {availableYears.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-600 flex-wrap">
            <span className="flex items-center gap-2">
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              {t('nav.since')} {data[0] ? `${t(data[0].monthKey)} ${data[0].year}` : 'mayo 2024'}
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="text-[#16A34A] font-semibold text-base">+{totalGrowth.toFixed(1)}%</span>
              <span className="text-gray-500">total</span>
            </span>
          </div>
        </div>

        {/* Chart Container - Full bleed with elegant spacing */}
        <div className="relative w-full p-6 pt-2" style={{ height: chartHeight }}>
          <svg
            width="100%"
            height={chartHeight}
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            className="w-full h-full"
            preserveAspectRatio="none"
          >
            <defs>
              {/* Slow animation mask */}
              <mask id="line-mask">
                <rect 
                  x="0" 
                  y="0" 
                  width={isAnimated ? "100%" : "0%"} 
                  height="100%" 
                  fill="white"
                  style={{
                    transition: 'width 1200ms cubic-bezier(0.4, 0, 0.2, 1)' // Much slower, elegant easing
                  }}
                />
              </mask>
              
              {/* Elegant gradient for area */}
              <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#16A34A" stopOpacity="0.12" />
                <stop offset="100%" stopColor="#16A34A" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            
            {/* Subtle horizontal grid lines */}
            {yTicks.map((_, i) => {
              // Grid lines from bottom to top (i=0 at bottom, i=4 at top)
              const y = margin.top + plotHeight - (i * (plotHeight / 4));
              return (
                <line
                  key={i}
                  x1={margin.left}
                  y1={y}
                  x2={margin.left + plotWidth}
                  y2={y}
                  stroke="#F1F5F9"
                  strokeWidth="1"
                  opacity="0.8"
                />
              );
            })}
            
            {/* Elegant area under line */}
            <path
              d={areaPath}
              fill="url(#areaGradient)"
              mask="url(#line-mask)"
              className="transition-all duration-1200 ease-out"
            />
            
            {/* Main line - premium styling */}
            <path
              d={pathData}
              fill="none"
              stroke="#16A34A"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              mask="url(#line-mask)"
              style={{
                filter: 'drop-shadow(0 1px 3px rgba(22, 163, 74, 0.1))'
              }}
            />
            
            {/* Interactive data points (12-month series) */}
            {data.map((d, i) => {
              const pos = getPointPosition(d.value, i);
              return (
                <Point
                  key={`${d.month}-${d.year}`}
                  cx={pos.x}
                  cy={pos.y}
                  data={d}
                  onHover={handlePointHover}
                  onMove={handlePointMove}
                  onLeave={handlePointLeave}
                  isHovered={hoveredPoint === d}
                />
              );
            })}
            
            {/* Y-axis labels - elegant typography */}
            {yTicks.map((tick, i) => {
              // Y positions: bottom to top (i=0 at bottom, i=4 at top)
              const y = margin.top + plotHeight - (i * (plotHeight / 4));
              return (
                <text
                  key={i}
                  x={margin.left - 16}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                  style={{ 
                    fontSize: '12px', 
                    fontWeight: '500', 
                    fill: '#64748B',
                    fontFamily: 'Inter, sans-serif',
                    fontFeatureSettings: '"tnum"'
                  }}
                >
                  {tick.label}
                </text>
              );
            })}
            
            {/* X-axis labels - refined spacing (12-month series) */}
            {(() => {
              const labelCount = windowWidth > 1400 ? 8 : 6;
              const interval = Math.max(1, Math.floor(data.length / labelCount));
              return data.map((d, i) => {
                if (i % interval !== 0) return null;
                const pos = getPointPosition(d.value, i);
                return (
                  <text
                    key={`${d.month}-${d.year}`}
                    x={pos.x}
                    y={chartHeight - margin.bottom + 24}
                    textAnchor="middle"
                    style={{
                      fontSize: windowWidth > 1400 ? '13px' : '12px',
                      fontWeight: '500',
                      fill: '#64748B',
                      fontFamily: 'Inter, sans-serif'
                    }}
                  >
                    {t(d.monthKey)}
                  </text>
                );
              });
            })()}
          </svg>
        </div>
      </div>

      {/* Premium Tooltip que sigue el cursor exactamente - Fuera del contenedor */}
      <Tooltip
        visible={hoveredPoint !== null}
        x={tooltipPos.x}
        y={tooltipPos.y}
        data={hoveredPoint}
      />
    </>
  );
}